import { useState, useEffect } from 'react';
import axios from 'axios';
import './AboutModal.css';

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MethodologyData {
  version: string;
  title: string;
  description: string;
  parameters: {
    land_coverage: {
      value: string;
      marketing_claim: string;
      reality: string;
      source: string;
    };
    panel_efficiency: {
      high: string;
      average: string;
      old: string;
      note: string;
    };
    cloud_reduction: {
      clear_sky: string;
      '25_percent_cloud': string;
      '50_percent_cloud': string;
      '75_percent_cloud': string;
      '100_percent_cloud': string;
      source: string;
    };
    temperature_derate: {
      rate: string;
      cell_temp_offset: string;
      example: string;
    };
    system_losses: {
      inverter: string;
      soiling: string;
      wiring: string;
      mismatch: string;
      transformer: string;
      shading_tilt: string;
      total: string;
    };
    homes_powered: {
      consumption: string;
      source: string;
      note: string;
    };
  };
  data_sources: string[];
  caveats: string[];
}

export function AboutModal({ isOpen, onClose }: AboutModalProps) {
  const [methodology, setMethodology] = useState<MethodologyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'parameters' | 'sources'>('overview');

  useEffect(() => {
    if (isOpen && !methodology) {
      fetchMethodology();
    }
  }, [isOpen]);

  const fetchMethodology = async () => {
    setLoading(true);
    try {
      const response = await axios.get<MethodologyData>('/api/methodology');
      setMethodology(response.data);
    } catch (error) {
      console.error('Failed to fetch methodology:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📊 Calculation Methodology</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          <button 
            className={activeTab === 'overview' ? 'active' : ''} 
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={activeTab === 'parameters' ? 'active' : ''} 
            onClick={() => setActiveTab('parameters')}
          >
            Parameters
          </button>
          <button 
            className={activeTab === 'sources' ? 'active' : ''} 
            onClick={() => setActiveTab('sources')}
          >
            Sources
          </button>
        </div>

        <div className="modal-body">
          {loading && <div className="loading">Loading methodology...</div>}
          
          {methodology && activeTab === 'overview' && (
            <div className="tab-content">
              <div className="methodology-intro">
                <p className="version">Version {methodology.version}</p>
                <p className="description">{methodology.description}</p>
              </div>

              <div className="key-points">
                <h3>🎯 Key Conservative Adjustments</h3>
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Marketing Claims</th>
                      <th>Our Estimate</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Land Coverage</td>
                      <td>50%</td>
                      <td><strong>{methodology.parameters.land_coverage.value}</strong></td>
                    </tr>
                    <tr>
                      <td>100% Cloud Irradiance</td>
                      <td>15-20%</td>
                      <td><strong>10%</strong></td>
                    </tr>
                    <tr>
                      <td>System Losses Total</td>
                      <td>5%</td>
                      <td><strong>{methodology.parameters.system_losses.total}</strong></td>
                    </tr>
                    <tr>
                      <td>Home Energy/Day</td>
                      <td>10 kWh</td>
                      <td><strong>{methodology.parameters.homes_powered.consumption}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="caveats-section">
                <h3>⚠️ Important Caveats</h3>
                <ul>
                  {methodology.caveats.map((caveat, i) => (
                    <li key={i}>{caveat}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {methodology && activeTab === 'parameters' && (
            <div className="tab-content">
              <div className="parameter-section">
                <h3>🏗️ Land Coverage</h3>
                <div className="param-detail">
                  <span className="param-value">{methodology.parameters.land_coverage.value}</span>
                  <p><strong>Marketing claim:</strong> {methodology.parameters.land_coverage.marketing_claim}</p>
                  <p><strong>Reality:</strong> {methodology.parameters.land_coverage.reality}</p>
                  <p className="source">Source: {methodology.parameters.land_coverage.source}</p>
                </div>
              </div>

              <div className="parameter-section">
                <h3>⚡ Panel Efficiency</h3>
                <div className="param-detail">
                  <ul>
                    <li><strong>Premium:</strong> {methodology.parameters.panel_efficiency.high}</li>
                    <li><strong>Standard:</strong> {methodology.parameters.panel_efficiency.average}</li>
                    <li><strong>Older:</strong> {methodology.parameters.panel_efficiency.old}</li>
                  </ul>
                  <p className="note">Note: {methodology.parameters.panel_efficiency.note}</p>
                </div>
              </div>

              <div className="parameter-section">
                <h3>☁️ Cloud Cover Impact</h3>
                <div className="param-detail">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Cloud Cover</th>
                        <th>Irradiance Retained</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>0%</td><td>{methodology.parameters.cloud_reduction.clear_sky}</td></tr>
                      <tr><td>25%</td><td>{methodology.parameters.cloud_reduction['25_percent_cloud']}</td></tr>
                      <tr><td>50%</td><td>{methodology.parameters.cloud_reduction['50_percent_cloud']}</td></tr>
                      <tr><td>75%</td><td>{methodology.parameters.cloud_reduction['75_percent_cloud']}</td></tr>
                      <tr><td>100%</td><td>{methodology.parameters.cloud_reduction['100_percent_cloud']}</td></tr>
                    </tbody>
                  </table>
                  <p className="source">Source: {methodology.parameters.cloud_reduction.source}</p>
                </div>
              </div>

              <div className="parameter-section">
                <h3>🌡️ Temperature Derate</h3>
                <div className="param-detail">
                  <ul>
                    <li><strong>Rate:</strong> {methodology.parameters.temperature_derate.rate}</li>
                    <li><strong>Cell temp offset:</strong> {methodology.parameters.temperature_derate.cell_temp_offset}</li>
                    <li><strong>Example:</strong> {methodology.parameters.temperature_derate.example}</li>
                  </ul>
                </div>
              </div>

              <div className="parameter-section">
                <h3>📉 System Losses (Multiplicative)</h3>
                <div className="param-detail">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Loss Type</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>Inverter</td><td>{methodology.parameters.system_losses.inverter}</td></tr>
                      <tr><td>Soiling</td><td>{methodology.parameters.system_losses.soiling}</td></tr>
                      <tr><td>Wiring/Cables</td><td>{methodology.parameters.system_losses.wiring}</td></tr>
                      <tr><td>Mismatch</td><td>{methodology.parameters.system_losses.mismatch}</td></tr>
                      <tr><td>Transformer</td><td>{methodology.parameters.system_losses.transformer}</td></tr>
                      <tr><td>Shading/Tilt</td><td>{methodology.parameters.system_losses.shading_tilt}</td></tr>
                      <tr className="total-row"><td><strong>Total</strong></td><td><strong>{methodology.parameters.system_losses.total}</strong></td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="parameter-section">
                <h3>🏠 Homes Powered</h3>
                <div className="param-detail">
                  <p><strong>Consumption:</strong> {methodology.parameters.homes_powered.consumption}</p>
                  <p><strong>Source:</strong> {methodology.parameters.homes_powered.source}</p>
                  <p className="note">Note: {methodology.parameters.homes_powered.note}</p>
                </div>
              </div>
            </div>
          )}

          {methodology && activeTab === 'sources' && (
            <div className="tab-content">
              <div className="sources-section">
                <h3>📚 Data Sources</h3>
                <ul className="source-list">
                  {methodology.data_sources.map((source, i) => (
                    <li key={i}>{source}</li>
                  ))}
                </ul>
              </div>

              <div className="formula-section">
                <h3>📐 Core Formula</h3>
                <div className="formula-box">
                  <code>
                    Power (MW) = Panel Area × Irradiance × Efficiency × System Losses
                  </code>
                </div>
                <div className="formula-breakdown">
                  <p><strong>Where:</strong></p>
                  <ul>
                    <li><em>Panel Area</em> = Land (acres) × 4046.86 m²/acre × 35% coverage</li>
                    <li><em>Irradiance</em> = Clear sky W/m² × Solar elevation × Cloud factor</li>
                    <li><em>Efficiency</em> = Panel % (10-15%)</li>
                    <li><em>System Losses</em> = (1-inverter) × (1-soiling) × (1-temp) × (1-additional)</li>
                  </ul>
                </div>
              </div>

              <div className="contact-section">
                <h3>🔍 Transparency</h3>
                <p>
                  The calculation methodology prioritizes robust and verifiable figures to ensure estimates reflect on-the-ground realities, rather than idealized assumptions. Estimation inputs are chosen with particular attention to alignment with authoritative industry references and regional datasets, supporting planning submissions and informed evaluation.
                </p>
                <p>
                  <strong>Weather data:</strong> When API key is configured, uses OpenWeatherMap. 
                  Otherwise, generates seasonally-appropriate UK sample data.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <span className="methodology-note">
            Conservative estimates for planning objection purposes
          </span>
          <button className="close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

